import { planShareLine } from './plantingPlanShare';

/**
 * My Plans library helpers. Plans map from saved area groups.
 * Card status is a client heuristic until planting-log events exist.
 * Lifecycle filter chips were removed — search + sort only.
 */

export const PLAN_LIBRARY_SORTS = Object.freeze([
  { id: 'recent', label: 'RECENT' },
  { id: 'date', label: 'PLANTING' },
  { id: 'acres', label: 'ACRES' },
]);

export const PLAN_STATUS = Object.freeze({
  draft: {
    id: 'draft',
    label: 'DRAFT',
    headBg: '#E8E1CB',
    headFg: '#221E18',
    prog: 0,
    progLabel: 'NOT SCHEDULED',
    progValue: '—',
    progFill: '#C9BF9B',
  },
  planned: {
    id: 'planned',
    label: 'PLANNED',
    headBg: '#C08A20',
    headFg: '#221E18',
    prog: 12,
    progLabel: 'DAYS TO PLANTING',
    progValue: '—',
    progFill: '#C08A20',
  },
  ground: {
    id: 'ground',
    label: 'IN THE GROUND',
    headBg: '#B4501C',
    headFg: '#F5EFDF',
    prog: 34,
    progLabel: 'DAYS TO BROWSE',
    progValue: '—',
    progFill: '#B4501C',
  },
  growing: {
    id: 'growing',
    label: 'GROWING',
    headBg: '#3B4E3A',
    headFg: '#EFE7D2',
    prog: 62,
    progLabel: 'DAYS TO PEAK',
    progValue: '—',
    progFill: '#3B4E3A',
  },
  wrapped: {
    id: 'wrapped',
    label: 'WRAPPED',
    headBg: '#8B8367',
    headFg: '#F5EFDF',
    prog: 100,
    progLabel: 'SEASON',
    progValue: 'CLOSED OUT',
    progFill: '#8B8367',
  },
});

/** Incomplete groups (no zone and no soil) count as drafts; saved complete groups as planned. */
export function resolvePlanStatus(group) {
  if (!group) return PLAN_STATUS.draft;
  const hasZone = Boolean(group.usda_zone_code);
  const hasSoil = Boolean(group.soil_type || group.soil_drainage);
  if (!hasZone && !hasSoil) return PLAN_STATUS.draft;
  return PLAN_STATUS.planned;
}

export function groupNeedsLime(group) {
  const ph = group?.soil_ph;
  if (ph === null || ph === undefined || ph === '') return false;
  const parsed = typeof ph === 'number' ? ph : parseFloat(ph);
  if (Number.isNaN(parsed)) return false;
  return parsed < 6.0;
}

/**
 * Acres shown on library cards / Ground hydrate.
 * Plot plans use denormalized total_acres. Town/acres plans prefer manual_acres
 * when total_acres was left stale after a PATCH.
 */
export function resolvePlanAcres(group) {
  if (!group) return 0;
  const plotCount = Number(group.polygon_count) || 0;
  const total = Number(group.total_acres);
  const manual = Number(group.manual_acres);
  if (plotCount > 0 && Number.isFinite(total) && total > 0) return total;
  if (Number.isFinite(manual) && manual > 0) return manual;
  if (Number.isFinite(total) && total > 0) return total;
  return 0;
}

/** Drop trailing "· 1.0 ac" from auto-named town plans. Acres belong on the meta line. */
export function stripPlanNameAcres(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  return raw.replace(/\s*[·•]\s*\d+(?:\.\d+)?\s*ac(?:res)?\s*$/i, '').trim();
}

export function buildLibraryStats(plans = [], visibleCount = 0) {
  const list = Array.isArray(plans) ? plans : [];
  const totalAcres = list.reduce((sum, plan) => sum + resolvePlanAcres(plan), 0);
  const needsLime = list.filter((plan) => groupNeedsLime(plan)).length;
  return [
    {
      id: 'plans',
      label: 'PLANS',
      value: String(list.length),
      note: `${Math.max(0, visibleCount)} SHOWING`,
      tone: 'paper',
    },
    {
      id: 'acres',
      label: 'ACRES PLANNED',
      value: totalAcres.toFixed(1),
      note: 'ACROSS ALL PLOTS',
      tone: 'paper',
    },
    {
      id: 'next',
      label: 'NEXT PLANTING',
      value: '—',
      note: 'TIMING SOON',
      tone: 'forest',
    },
    {
      id: 'lime',
      label: 'NEEDS LIME',
      value: String(needsLime),
      note: 'ORDER SIX WEEKS OUT',
      tone: 'paper',
    },
  ];
}

export function filterAndSortPlans(plans = [], { query = '', sort = 'recent' } = {}) {
  const needle = String(query || '').trim().toLowerCase();
  let list = (Array.isArray(plans) ? plans : []).filter((plan) => {
    if (!needle) return true;
    const haystack = [
      plan.name,
      plan.note,
      plan.usda_zone_code,
      plan.planting_season,
      plan.soil_type,
      plan.soil_drainage,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });

  list = list.slice().sort((a, b) => {
    if (sort === 'acres') {
      return resolvePlanAcres(b) - resolvePlanAcres(a);
    }
    if (sort === 'date') {
      const aTime = a.updated || a.created || '';
      const bTime = b.updated || b.created || '';
      return String(bTime).localeCompare(String(aTime));
    }
    // recent — newest updated/created first
    const aTime = a.updated || a.created || '';
    const bTime = b.updated || b.created || '';
    return String(bTime).localeCompare(String(aTime));
  });

  return list;
}

export function buildPlanCardModel(group, { isSelected = false, clubNames = {} } = {}) {
  const status = resolvePlanStatus(group);
  const acresValue = resolvePlanAcres(group);
  const acres = acresValue > 0 ? acresValue.toFixed(1) : '—';
  const plotCount = group.polygon_count || 0;
  const zone = group.usda_zone_code
    ? `ZONE ${String(group.usda_zone_code).toUpperCase()}`
    : 'ZONE —';
  const scope = group.club ? 'CLUB' : 'PERSONAL';
  const season = group.planting_season
    ? String(group.planting_season).toUpperCase()
    : '';
  const lime = groupNeedsLime(group);
  return {
    id: group.id,
    name: stripPlanNameAcres(group.name) || 'Untitled plan',
    statusLabel: status.label,
    headBg: status.headBg,
    headFg: status.headFg,
    zone,
    meta: `${acres} AC · ${plotCount} PLOT${plotCount === 1 ? '' : 'S'}`,
    isSelected,
    chips: [
      { label: scope, tone: 'muted' },
      ...(season ? [{ label: season, tone: 'muted' }] : []),
      lime
        ? { label: 'LIME FIRST', tone: 'gold' }
        : { label: 'NO LIME NEEDED', tone: 'muted' },
    ],
    progLabel: status.progLabel,
    progValue: status.progValue,
    progPct: status.prog,
    progFill: status.progFill,
    shareLine: planShareLine(group, clubNames),
    group,
  };
}
